import React from 'react';
import DataCard from '../common/DataCard';
import Loader from '../common/Loader';
import Pagination from '../common/Pagination';
import { convertToUserTime } from '../../utils/dateUtils/convertToUserTime';
import IconButton from '../common/IconButton';
import { MessageCircleWarning, NotebookPen, Pen, Trash } from 'lucide-react';
import { ActivityDTO } from '../../services/apiClient';

import Badge from '../common/Badge';
import { getBadgeColors } from '../../utils/displayUtils';
import { renderCaliberBadge } from '../../utils/firearmDisplayUtils';
import Tippy from '@tippyjs/react';
import { ActivityTypeConstants } from '../../constants/ActivityTypeConstants';
import { FirearmCaliberConstants } from '../../constants/FirearmCaliberConstants';
import { FirearmMakeConstants } from '../../constants/FirearmMakeConstants';
import { ActivityStatusConstants } from '../../constants/ActivityStatusConstants';
import { useFeedbackDrawer } from '../../context/FeedbackDrawerContext';
import { useActivityApi } from '../../hooks/useActivityApi';

interface MemberActivityDataCardProps {
    data: ActivityDTO[];
    handleEdit: (row: ActivityDTO) => void;
    handleDelete: (row: ActivityDTO) => void;
    currentPage: number;
    totalPages: number;
    setCurrentPage: (page: number) => void;
    pageSize: number;
    setPageSize: (size: number) => void;
    isLoading: boolean;

    // Optional action renderers injected by the List page
    renderHeaderActions?: (row: ActivityDTO) => React.ReactNode;
    renderPrimaryActions?: (row: ActivityDTO) => React.ReactNode;
    renderSecondaryActions?: (row: ActivityDTO) => React.ReactNode;
}

const MemberActivityDataCard: React.FC<MemberActivityDataCardProps> = ({
    data,
    handleEdit,
    handleDelete,
    currentPage,
    totalPages,
    setCurrentPage,
    pageSize,
    setPageSize,
    isLoading,
    renderHeaderActions,
    renderPrimaryActions,
    renderSecondaryActions,
}) => {
    const { showFeedback } = useFeedbackDrawer();
    const { getFeedback } = useActivityApi();

    return (
        <div>
            {isLoading ? (
                <div className="py-8 flex justify-center"><Loader /></div>
            ) : data.length === 0 ? (
                <div className="py-8 text-center text-sm text-gray-500">No records found</div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                    {data.map((row) => {


                        const leftBadge = (row: ActivityDTO) => {
                            const base = row.activityStatus?.colourGrading?.colour ?? '#ffffffff';
                            const { bg, text } = getBadgeColors(base, 0.18, 0.40);
                            const title = row.activityStatus?.title || 'Unknown';

                            return base ? (
                                <Badge
                                    label={title}
                                    variant="custom"
                                    customBgColor={bg}
                                    customTextColor={text}
                                />
                            ) : (
                                <span>{row.activityStatus?.title || 'Unknown'}</span>
                            );
                        };

                        const title = row.activityType?.title || 'Unknown Firearm';

                        return (
                            <DataCard<ActivityDTO>
                                key={row.id}
                                item={row}
                                collapsibleOnMobile={true}
                                leftBadgeNode={() => leftBadge(row)}
                                rightMeta={() => {
                                    return {
                                        label: 'Applied',
                                        value:
                                            <span>
                                                {row.createdUtc ? convertToUserTime(String(row.createdUtc), true) : 'n/a'}
                                                {row.complianceYearDisplay ? ` [for ${row.complianceYearDisplay}]` : ''}
                                            </span>,
                                    };
                                }}
                                title={() => title}
                                headerActions={renderHeaderActions}
                                fields={[
                                    {
                                        label: 'Type of Activity',
                                        hideIf: (row) => !row.activityCategory?.title && !row.nonActiveType?.title,
                                        render: (row) => row.activityCategory?.title ?? row.nonActiveType?.title ?? ''
                                    },
                                    {
                                        label: 'Date',
                                        hideIf: (row) => !row.activityDate && !row.dateYear,
                                        render: (row) => row.activityDate ? convertToUserTime(String(row.activityDate), true) : row.dateYear ?? ''
                                    },
                                    {
                                        label: 'Location',
                                        hideIf: (row) => row.activityTypeId === ActivityTypeConstants.NON_ACTIVE_REPORT
                                            ? !row.country?.title
                                            : !row.country?.title && !row.province?.title && !row.town && !row.location,
                                        render: (row) => row.activityTypeId === ActivityTypeConstants.NON_ACTIVE_REPORT
                                            ? row.country?.title || ''
                                            : [row.country?.title, row.province?.title, row.town, row.location].filter(Boolean).join(', ')
                                    },
                                    {
                                        label: 'Firearm',
                                        hideIf: (row) => row.memberFirearm == null,
                                        render: (row) => {
                                            const make = row.memberFirearm?.firearmMakeId === FirearmMakeConstants.NOT_ON_LIST ? row.memberFirearm?.firearmMakeOther : row.memberFirearm?.firearmMake?.title;
                                            const type = row.memberFirearm?.firearmType?.title;
                                            const caliber = renderCaliberBadge(
                                                row.memberFirearm?.firearmCaliberId === FirearmCaliberConstants.NOT_ON_LIST ? row.memberFirearm?.caliberOther : row.memberFirearm?.caliber?.title,
                                                row.memberFirearm?.firearmSpecificId
                                            );
                                            return (
                                                <div className="flex items-center gap-1 flex-wrap">
                                                    {[make, type].filter(Boolean).map((item, idx) => (
                                                        <React.Fragment key={idx}>
                                                            {idx > 0 && <span>, </span>}
                                                            <span>{item}</span>
                                                        </React.Fragment>
                                                    ))}
                                                    {caliber && (
                                                        <>
                                                            {(make || type) && <span>, </span>}
                                                            {caliber}
                                                        </>
                                                    )}
                                                </div>
                                            );
                                        }
                                    },
                                ]}
                                files={
                                    [
                                        { hideIf: () => row.confirmationFileId === undefined, label: 'Confirmation Document', id: row.confirmationFileId ?? '' },
                                        { hideIf: () => row.additionalConfirmationFile1Id === undefined, label: 'Confirmation Document Additional 1', id: row.additionalConfirmationFile1Id ?? '' },
                                        { hideIf: () => row.additionalConfirmationFile2Id === undefined, label: 'Confirmation Document Additional 2', id: row.additionalConfirmationFile2Id ?? '' },
                                    ]}
                                primaryActions={
                                    renderPrimaryActions ??
                                    ((row) => (
                                        <div className="flex justify-between w-full">
                                            <div className="flex gap-3">
                                                {row.activityStatus?.title?.includes('feedback') && (
                                                    <IconButton
                                                        label='Feedback'
                                                        color="blue"
                                                        icon={<MessageCircleWarning />}
                                                        onClick={() => showFeedback(() => getFeedback(row.id!))} />
                                                )}
                                                {row.notes && (
                                                    <IconButton
                                                        label='My Notes'
                                                        content={row.notes}
                                                        color="blue"
                                                        icon={<NotebookPen />} />
                                                )}
                                            </div>
                                            <div className="flex gap-3">
                                                {(row.activityStatusId === ActivityStatusConstants.PENDING || row.activityStatusId === ActivityStatusConstants.PENDING_UPDATED  || row.activityStatusId === ActivityStatusConstants.REVIEWED_WITH_FEEDBACK) && (
                                                    <>
                                                        <IconButton
                                                            label='Edit'
                                                            color="orange" icon={<Pen />}
                                                            onClick={() => handleEdit(row)} />

                                                        <IconButton
                                                            label='Delete'
                                                            color="red"
                                                            icon={<Trash />}
                                                            onClick={() => handleDelete(row)} />
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    ))
                                }
                                secondaryActions={
                                    row.activityStatus?.title?.includes('Approved')
                                        ? renderSecondaryActions
                                        : undefined
                                }
                            />
                        );
                    })}
                </div>
            )}

            {!isLoading && data.length > 0 && (
                <div className="mt-4">
                    <Pagination
                        currentPage={Number.isFinite(currentPage) && currentPage > 0 ? currentPage : 1}
                        totalPages={Number.isFinite(totalPages) && totalPages > 0 ? totalPages : 1}
                        onPageChange={setCurrentPage}
                        pageSize={Number.isFinite(pageSize) && pageSize > 0 ? pageSize : 12}
                        onPageSizeChange={setPageSize}
                    />
                </div>
            )}
        </div>
    );
};

export default MemberActivityDataCard;
